import { Activity, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Link, useNavigate } from 'react-router-dom'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import fetchWithAuth from '../../utilities/fetchWithAuth'

const CreateDataTypePage = () => {
  const [dataTypeData, setDataTypeData] = useState({
    name: '',
    subtype: 'file',
  })
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const navigate = useNavigate()

  const validate = () => {
    const newErrors = {}

    if (!dataTypeData.name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!dataTypeData.subtype.trim()) {
      newErrors.subtype = 'Subtype is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e) => {
    setDataTypeData({
      ...dataTypeData,
      [e.target.name]: e.target.value,
    })

    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: undefined,
      })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await fetchWithAuth('data-types', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataTypeData),
      })

      if (response.status === 201) {
        navigate('/data-types')
      } else {
        const data = await response.json()
        if (data.errors) {
          const apiErrors = {}
          data.errors.forEach((err) => {
            apiErrors[err.field] = err.message
          })
          setErrors(apiErrors)
        } else {
          setError(data.message || 'Failed to create data type')
        }
      }
    } catch (error) {
      console.error('Error creating data type:', error)
      setError('Failed to create data type. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <IconButton component={Link} to="/data-types" edge="start">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5">Create New Data Type</Typography>
      </Stack>

      <Activity mode={error ? 'visible' : 'hidden'}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Activity>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <TextField
              label="Name"
              name="name"
              value={dataTypeData.name}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.name}
              helperText={errors.name}
            />

            <FormControl fullWidth required error={!!errors.subtype}>
              <InputLabel id="subtype-label" htmlFor="subtype">
                Subtype
              </InputLabel>
              <Select
                labelId="subtype-label"
                name="subtype"
                value={dataTypeData.subtype}
                onChange={handleChange}
                label="Subtype"
                inputProps={{ id: 'subtype' }}
                disabled
              >
                <MenuItem value="file">File</MenuItem>
              </Select>
              <Activity mode={errors.subtype ? 'visible' : 'hidden'}>
                <FormHelperText>{errors.subtype}</FormHelperText>
              </Activity>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
              >
                {loading ? 'Creating...' : 'Create Data Type'}
              </Button>
              <Button
                variant="outlined"
                component={Link}
                to="/data-types"
                disabled={loading}
              >
                Cancel
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  )
}

export default CreateDataTypePage
